import { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { Link } from "react-router-dom";

import { Tooltip } from "@/components/Tooltip";
import type { WidgetComponentProps } from "../types";
import { useInsightsQuery } from "../useInsightsQuery";

function formatLap(seconds: number | null | undefined): string {
  if (seconds == null) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toFixed(2).padStart(5, "0")}`;
}

export function MissionProgressWidget({ sizeVariant }: WidgetComponentProps) {
  const { data, isLoading } = useInsightsQuery();

  const chartOption = useMemo(() => {
    const trend = data?.mission_progress.trend ?? [];
    const validPoints = trend.filter((p) => p.best_lap_seconds != null);
    const labels = validPoints.map((_, i) => `#${i + 1}`);
    const values = validPoints.map((p) => p.best_lap_seconds as number);
    const rollingBest = values.reduce<number[]>((acc, v, i) => {
      const prev = i === 0 ? v : acc[i - 1];
      acc.push(Math.min(prev, v));
      return acc;
    }, []);

    return {
      grid: { top: 10, right: 12, bottom: 22, left: 40 },
      xAxis: {
        type: "category",
        data: labels,
        axisLabel: { color: "#A0AAB8", fontSize: 10 },
        axisLine: { lineStyle: { color: "#2A3444" } },
        axisTick: { show: false },
      },
      yAxis: {
        type: "value",
        inverse: true, // lower lap time = better = higher on chart
        axisLabel: {
          color: "#A0AAB8",
          fontSize: 10,
          formatter: (v: number) => formatLap(v),
        },
        axisLine: { show: false },
        splitLine: { lineStyle: { color: "#2A3444", type: "dashed" } },
      },
      tooltip: {
        trigger: "axis",
        backgroundColor: "rgba(10, 14, 20, 0.96)",
        borderColor: "#2A3444",
        textStyle: { color: "#E8ECF1", fontSize: 12 },
        valueFormatter: (v: number) => formatLap(v),
      },
      series: [
        {
          name: "Best lap",
          type: "line",
          data: values,
          smooth: 0.25,
          showSymbol: false,
          lineStyle: { color: "#FF6B1A", width: 2 },
          areaStyle: { color: "rgba(255, 107, 26, 0.1)" },
        },
        {
          name: "Personal best",
          type: "line",
          data: rollingBest,
          showSymbol: false,
          lineStyle: { color: "#4ADE80", width: 1.5, type: "dashed" },
        },
      ],
    };
  }, [data]);

  if (isLoading) return <p className="widget-empty">Loading…</p>;
  const mp = data?.mission_progress;
  if (!mp || !mp.mission_name) {
    return <p className="widget-empty">No mission data yet.</p>;
  }

  const attempts = mp.attempts;
  const improvementText =
    mp.improvement_seconds != null
      ? mp.improvement_seconds >= 0
        ? `-${mp.improvement_seconds.toFixed(2)}s faster`
        : `+${Math.abs(mp.improvement_seconds).toFixed(2)}s slower`
      : "—";

  const trendPoints = mp.trend.filter((p) => p.best_lap_seconds != null).length;

  return (
    <div className={`mission-progress mission-progress--${sizeVariant}`}>
      <div className="mission-progress__head">
        <div>
          <p className="eyebrow">
            Mission progress
            <Tooltip
              content={
                <>
                  <strong>{mp.mission_name}</strong>
                  <span>
                    Your best lap over time. Orange = per-attempt best;
                    green dashed = rolling personal best.
                  </span>
                </>
              }
            >
              <span className="info-hint" aria-label="About mission progress">i</span>
            </Tooltip>
          </p>
          <h3 className="mission-progress__title">{mp.mission_name}</h3>
        </div>
        {mp.rank && (
          <span className="mission-progress__rank">
            Rank <strong>#{mp.rank}</strong>
          </span>
        )}
      </div>

      <div className="mission-progress__stats">
        <div className="mission-progress__stat">
          <span className="mission-progress__stat-lbl">Best lap</span>
          <span className="mission-progress__stat-val">
            {formatLap(mp.best_lap_seconds)}
          </span>
        </div>
        <div className="mission-progress__stat">
          <span className="mission-progress__stat-lbl">Attempts</span>
          <span className="mission-progress__stat-val">{attempts}</span>
        </div>
        <div className="mission-progress__stat">
          <span className="mission-progress__stat-lbl">Laps done</span>
          <span className="mission-progress__stat-val">{mp.completed_laps}</span>
        </div>
        <div className="mission-progress__stat">
          <span className="mission-progress__stat-lbl">Trend</span>
          <span
            className={`mission-progress__stat-val ${
              mp.improvement_seconds != null && mp.improvement_seconds > 0
                ? "mission-progress__stat-val--good"
                : ""
            }`}
          >
            {improvementText}
          </span>
        </div>
      </div>

      {trendPoints >= 2 && (
        <div className="mission-progress__chart">
          <ReactECharts option={chartOption} notMerge style={{ height: "100%", width: "100%" }} />
        </div>
      )}

      {mp.best_lap_flight_id && (
        <Link to={`/flights/${mp.best_lap_flight_id}`} className="text-link">
          Open best attempt →
        </Link>
      )}
    </div>
  );
}
